import { X } from "lucide-react";
import { toast } from "sonner";
import {
  useAddManufacturerAlias,
  useRemoveManufacturerAlias,
  useUpdateCustomPricing,
  useUpdateQuoteTracking,
  type ManufacturerAlias,
} from "@/api/manufacturer-tracking";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { SettingCell } from "@/components/setting-cell";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function errorDetail(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}

/**
 * Tracked for quoting vs the storefront, and the names retailers sell the brand under (USA-1105).
 * A manufacturer that isn't from AQ can be priced on each quote instead of from a catalog price (USA-1107).
 */
export function TrackingPanel({
  manufacturerUuid,
  manufacturerName,
  quoteTracking,
  storefrontTracking,
  customPricing,
  fromAutoQuotes,
  aliases,
}: {
  manufacturerUuid: string;
  manufacturerName: string;
  quoteTracking: boolean;
  storefrontTracking: boolean;
  customPricing: boolean;
  fromAutoQuotes: boolean;
  aliases: ManufacturerAlias[];
}) {
  const updateQuoting = useUpdateQuoteTracking(manufacturerUuid);
  const updateCustomPricing = useUpdateCustomPricing(manufacturerUuid);
  const addAlias = useAddManufacturerAlias(manufacturerUuid);
  const removeAlias = useRemoveManufacturerAlias(manufacturerUuid);

  const quotingSwitch = (
    <Switch
      id="mfr-quoting"
      checked={quoteTracking || storefrontTracking}
      disabled={storefrontTracking || updateQuoting.isPending}
      onCheckedChange={(checked) =>
        updateQuoting.mutate(checked, {
          onError: (error) =>
            toast.error(
              errorDetail(error, `Could not update quoting for ${manufacturerName}`),
            ),
        })
      }
    />
  );

  return (
    <section
      aria-labelledby="mfr-tracking"
      className="overflow-hidden rounded-xl border bg-card"
    >
      <div className="border-b px-4 py-2">
        <h2 id="mfr-tracking" className="text-sm font-semibold">
          Tracking
        </h2>
      </div>
      <div
        className={
          fromAutoQuotes
            ? "-mb-px -mr-px grid grid-cols-2 lg:grid-cols-[repeat(2,minmax(9rem,auto))_minmax(0,1fr)]"
            : "-mb-px -mr-px grid grid-cols-3 lg:grid-cols-[repeat(3,minmax(9rem,auto))_minmax(0,1fr)]"
        }
      >
        <SettingCell label="Quoting" htmlFor="mfr-quoting">
          {storefrontTracking ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex rounded-full">
                  {quotingSwitch}
                </span>
              </TooltipTrigger>
              <TooltipContent>Always on for storefront manufacturers</TooltipContent>
            </Tooltip>
          ) : (
            quotingSwitch
          )}
          <span className="text-sm">
            {quoteTracking || storefrontTracking ? "On" : "Off"}
          </span>
        </SettingCell>
        <SettingCell label="Storefront">
          <span className="text-sm">{storefrontTracking ? "On" : "Off"}</span>
        </SettingCell>
        {!fromAutoQuotes && (
          <SettingCell label="Priced on each quote" htmlFor="mfr-custom-pricing">
            <Switch
              id="mfr-custom-pricing"
              checked={customPricing}
              disabled={updateCustomPricing.isPending}
              onCheckedChange={(checked) =>
                updateCustomPricing.mutate(checked, {
                  onError: (error) =>
                    toast.error(
                      errorDetail(error, `Could not update pricing for ${manufacturerName}`),
                    ),
                })
              }
            />
            <span className="text-sm">{customPricing ? "On" : "Off"}</span>
          </SettingCell>
        )}
        <SettingCell
          label="Retailer names"
          className={fromAutoQuotes ? "col-span-2 lg:col-span-1" : "col-span-3 lg:col-span-1"}
        >
          <ul className="flex min-w-0 flex-wrap items-center gap-1.5">
            {aliases.map((alias) => (
              <li
                key={alias.id}
                className="inline-flex max-w-full items-center gap-0.5 rounded-full border bg-muted/40 py-0.5 pl-2.5 pr-0.5 text-sm"
              >
                <span className="truncate">{alias.alias}</span>
                <button
                  type="button"
                  aria-label={`Remove ${alias.alias}`}
                  className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() =>
                    removeAlias.mutate(alias.id, {
                      onError: (error) =>
                        toast.error(
                          errorDetail(error, `Could not remove ${alias.alias}`),
                        ),
                    })
                  }
                >
                  <X className="size-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
            <li>
              <POInlineEditableField
                value=""
                placeholder="Add name"
                ariaLabel="Add a retailer name"
                pending={addAlias.isPending}
                onConfirm={(raw) => {
                  const alias = raw.trim();
                  if (!alias) return;
                  addAlias.mutate(alias, {
                    onError: (error) =>
                      toast.error(errorDetail(error, `Could not add ${alias}`)),
                  });
                }}
              />
            </li>
          </ul>
        </SettingCell>
      </div>
    </section>
  );
}
